import { useDraggable } from '@dnd-kit/core'
import {
  Hash, LayoutGrid, BarChart3, LineChart, PieChart, Circle,
  BarChartHorizontal, Table2, Rss, Gauge, Tags,
} from 'lucide-react'

import { DashboardWidgetType } from '@/types/enums'

import { ActivityFeedWidget } from './widgets/ActivityFeedWidget'
import { BarChartWidget } from './widgets/BarChartWidget'
import { DataTableWidget } from './widgets/DataTableWidget'
import { DonutChartWidget } from './widgets/DonutChartWidget'
import { LineChartWidget } from './widgets/LineChartWidget'
import { NumberCardGroupWidget } from './widgets/NumberCardGroupWidget'
import { NumberCardWidget } from './widgets/NumberCardWidget'
import { PieChartWidget } from './widgets/PieChartWidget'
import { ProgressWidget } from './widgets/ProgressWidget'
import { StackedBarWidget } from './widgets/StackedBarWidget'
import { StatusBadgeGroupWidget } from './widgets/StatusBadgeGroupWidget'

interface WidgetTypeConfig {
  type: DashboardWidgetType
  label: string
  description: string
  icon: React.ElementType
  category: 'Metrics' | 'Charts' | 'Data' | 'System'
  renderPreview: () => React.ReactNode
}

const DUMMY_METRIC = { label: 'Total Sales', value: 12450 }
const DUMMY_METRICS = [
  { label: 'Active', value: 12 },
  { label: 'Pending', value: 5 },
  { label: 'Closed', value: 89 },
]
const DUMMY_SERIES = [
  { label: 'Mon', value: 10 },
  { label: 'Tue', value: 15 },
  { label: 'Wed', value: 8 },
]
const DUMMY_TABLE = [
  { id: '1', name: 'Alice', status: 'Active' },
  { id: '2', name: 'Bob', status: 'Pending' },
]
const DUMMY_ACTIVITY = [
  { id: '1', actorName: 'System', actionType: 'UPDATE', createdAt: new Date().toISOString() },
  { id: '2', actorName: 'Alice', actionType: 'CREATE', createdAt: new Date(Date.now() - 3600000).toISOString() },
]

const WIDGET_CONFIG: Record<DashboardWidgetType, WidgetTypeConfig> = {
  [DashboardWidgetType.NUMBER_CARD]: {
    type: DashboardWidgetType.NUMBER_CARD, label: 'Number Card', description: 'One key metric',
    icon: Hash, category: 'Metrics',
    renderPreview: () => <NumberCardWidget title="" metrics={[DUMMY_METRIC]} />
  },
  [DashboardWidgetType.NUMBER_CARD_GROUP]: {
    type: DashboardWidgetType.NUMBER_CARD_GROUP, label: 'Metric Group', description: 'Several metrics together',
    icon: LayoutGrid, category: 'Metrics',
    renderPreview: () => <NumberCardGroupWidget title="" metrics={DUMMY_METRICS} />
  },
  [DashboardWidgetType.BAR_CHART]: {
    type: DashboardWidgetType.BAR_CHART, label: 'Bar Chart', description: 'Compare values across categories',
    icon: BarChart3, category: 'Charts',
    renderPreview: () => <BarChartWidget title="" data={DUMMY_SERIES} />
  },
  [DashboardWidgetType.LINE_CHART]: {
    type: DashboardWidgetType.LINE_CHART, label: 'Line Chart', description: 'Trend over time',
    icon: LineChart, category: 'Charts',
    renderPreview: () => <LineChartWidget title="" data={DUMMY_SERIES} />
  },
  [DashboardWidgetType.PIE_CHART]: {
    type: DashboardWidgetType.PIE_CHART, label: 'Pie Chart', description: 'Proportion of a whole',
    icon: PieChart, category: 'Charts',
    renderPreview: () => <PieChartWidget title="" data={DUMMY_SERIES} />
  },
  [DashboardWidgetType.DONUT_CHART]: {
    type: DashboardWidgetType.DONUT_CHART, label: 'Donut Chart', description: 'Proportion, ring style',
    icon: Circle, category: 'Charts',
    renderPreview: () => <DonutChartWidget title="" data={DUMMY_SERIES} />
  },
  [DashboardWidgetType.STACKED_BAR]: {
    type: DashboardWidgetType.STACKED_BAR, label: 'Stacked Bar', description: 'Composition across categories',
    icon: BarChartHorizontal, category: 'Charts',
    renderPreview: () => <StackedBarWidget title="" data={DUMMY_SERIES} />
  },
  [DashboardWidgetType.TABLE]: {
    type: DashboardWidgetType.TABLE, label: 'Data Table', description: 'Raw rows in a grid',
    icon: Table2, category: 'Data',
    renderPreview: () => <DataTableWidget title="" rows={DUMMY_TABLE} widgetId="" dataSource="" dataSourceConfig={{}} />
  },
  [DashboardWidgetType.ACTIVITY_FEED]: {
    type: DashboardWidgetType.ACTIVITY_FEED, label: 'Activity Feed', description: 'Recent events, newest first',
    icon: Rss, category: 'Data',
    renderPreview: () => <ActivityFeedWidget title="" items={DUMMY_ACTIVITY} widgetId="" dataSource="" dataSourceConfig={{}} />
  },
  [DashboardWidgetType.PROGRESS_BAR]: {
    type: DashboardWidgetType.PROGRESS_BAR, label: 'Progress', description: 'Usage against a limit',
    icon: Gauge, category: 'System',
    renderPreview: () => <ProgressWidget title="" usage={{ workspaceId: '1', subscriptionStatus: 'ACTIVE', currentPeriodStart: null, currentPeriodEnd: null, usage: [{ featureKey: 'users', name: 'Users', currentUsage: 5, limitValue: 10, limitType: 'HARD' }] }} />
  },
  [DashboardWidgetType.STATUS_BADGE_GROUP]: {
    type: DashboardWidgetType.STATUS_BADGE_GROUP, label: 'Status Badges', description: 'Counts by status',
    icon: Tags, category: 'Metrics',
    renderPreview: () => <StatusBadgeGroupWidget title="" data={DUMMY_SERIES} />
  },
}

const CATEGORY_STYLES: Record<string, { gradient: string; bg: string; text: string }> = {
  Metrics: { gradient: 'from-blue-500 to-indigo-500', bg: 'bg-blue-50/50', text: 'text-blue-700' },
  Charts: { gradient: 'from-purple-500 to-violet-500', bg: 'bg-purple-50/50', text: 'text-purple-700' },
  Data: { gradient: 'from-amber-500 to-orange-500', bg: 'bg-amber-50/50', text: 'text-amber-700' },
  System: { gradient: 'from-cyan-500 to-teal-500', bg: 'bg-cyan-50/50', text: 'text-cyan-700' },
}

const PALETTE_CATEGORIES: { name: WidgetTypeConfig['category']; items: WidgetTypeConfig[] }[] = [
  { name: 'Metrics', items: Object.values(WIDGET_CONFIG).filter(c => c.category === 'Metrics') },
  { name: 'Charts', items: Object.values(WIDGET_CONFIG).filter(c => c.category === 'Charts') },
  { name: 'Data', items: Object.values(WIDGET_CONFIG).filter(c => c.category === 'Data') },
  { name: 'System', items: Object.values(WIDGET_CONFIG).filter(c => c.category === 'System') },
]

interface DashboardWidgetPickerProps {
  onAddWidget: (type: DashboardWidgetType) => void
}

function PaletteDragItem({ cfg, onAddWidget }: { cfg: WidgetTypeConfig; onAddWidget: (type: DashboardWidgetType) => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `dashboard-palette-${cfg.type}`,
    data: { source: 'dashboard-palette', widgetType: cfg.type },
  })
  const Icon = cfg.icon
  const catStyle = CATEGORY_STYLES[cfg.category]

  return (
    <div
      ref={setNodeRef} {...attributes} {...listeners}
      onClick={() => onAddWidget(cfg.type)}
      className={`
        flex flex-col gap-2 rounded-lg border px-3 py-2.5 cursor-grab active:cursor-grabbing
        select-none transition-all duration-150 bg-white
        ${isDragging ? 'opacity-40 scale-95' : 'hover:shadow-md hover:border-primary-300 hover:-translate-y-0.5'}
      `}
    >
      <div className="flex items-center gap-2">
        <div className={`flex h-6 w-6 items-center justify-center rounded-md bg-gradient-to-r ${catStyle.gradient} text-white`}>
          <Icon className="h-3 w-3" />
        </div>
        <p className="text-sm font-medium leading-tight text-neutral-700">{cfg.label}</p>
      </div>
      <div className="pointer-events-none h-[140px] overflow-hidden rounded-md border border-neutral-100 bg-neutral-50 p-1 opacity-75">
        <div className="scale-[0.8] origin-top-left w-[125%] h-[125%]">
          {cfg.renderPreview()}
        </div>
      </div>
    </div>
  )
}

export function DashboardWidgetPicker({ onAddWidget }: DashboardWidgetPickerProps) {
  return (
    <aside className="flex w-full shrink-0 flex-col gap-3 rounded-2xl border border-primary-100/50 bg-gradient-to-b from-white to-primary-50/30 p-4 shadow-sm lg:w-80">
      <div className="relative">
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-gradient-to-br from-primary-500 to-primary-600 text-white text-xs font-bold">+</div>
          <p className="text-xs font-bold uppercase tracking-widest text-neutral-400">Widget Library</p>
        </div>
        <p className="mt-1 ml-8 text-xs text-neutral-400">Drag onto the dashboard or click to add</p>
      </div>
      <div className="flex flex-col gap-6 overflow-y-auto max-h-[calc(100vh-220px)] pr-2 pb-4 scrollbar-thin">
        {PALETTE_CATEGORIES.map((category) => {
          const catStyle = CATEGORY_STYLES[category.name]
          return (
            <div key={category.name} className="flex flex-col gap-2">
              <div className={`flex items-center gap-1.5 rounded-md ${catStyle.bg} px-2 py-1`}>
                <div className={`h-1.5 w-1.5 rounded-full bg-gradient-to-r ${catStyle.gradient}`} />
                <p className={`text-[10px] font-bold uppercase tracking-widest ${catStyle.text}`}>{category.name}</p>
              </div>
              {category.items.map((cfg) => (
                <PaletteDragItem key={cfg.type} cfg={cfg} onAddWidget={onAddWidget} />
              ))}
            </div>
          )
        })}
      </div>
    </aside>
  )
}

export { WIDGET_CONFIG }
